import { Fragment, useEffect, useMemo, useState } from 'react';
import { Spinner } from '@fluentui/react-components';
import { getApuIaConceptMatrix, type ApuIaConcepto, type ApuIaSubReceta } from '../api/apuIa.js';
import type { QuoteConcept, QuoteConceptInsumo } from '../api/quotes.js';
import { formatoDeMoneda } from '../lib/money.js';
import {
  cantidadEquipoPorUnidad,
  cantidadCuadrillaPorUnidad,
  comerciosDelRenglon,
  contarOrigenesClasificados,
  fuenteActivaDelPrecio,
  origenDelPrecio,
  type OrigenPrecio,
  type RenglonDeInsumo,
} from '@supernova/shared';

/**
 * Análisis de precio unitario READ-ONLY de un concepto del presupuesto.
 *  - Matcheado (catálogo): agrupa `concepto.insumos` por tipo de recurso (ya viene
 *    en el listado, sin fetch).
 *  - Sintético (APU-IA): trae la matriz con getApuIaConceptMatrix y la muestra.
 * Nunca editable (a diferencia de la vista de APUs). Estilos inline theme-aware.
 */

// LOS IMPORTES VAN EN LA MONEDA DE LA OBRA. Antes este archivo pegaba un '$' fijo
// y formateaba en es-MX/MXN: el desglose de un presupuesto chileno se leía en pesos
// mexicanos. La moneda llega por prop desde la pantalla que abre el desglose
// (`moneda`), y si no se sabe el número va sin símbolo. Ver lib/money.ts.
const num = (n: number | null | undefined, d = 2): string =>
  (n ?? 0).toLocaleString('es-MX', { maximumFractionDigits: d });
// Redondear-luego-sumar: cada importe de renglón a 2 decimales antes de sumar,
// para que los renglones cuadren con el subtotal y con m1 (mismo criterio que
// round2 de @supernova/quotes; local para no arrastrar deps de node al web).
const round2 = (n: number): number =>
  Number.isFinite(n) ? Math.round((n + Number.EPSILON) * 100) / 100 : 0;

/**
 * DE DÓNDE SALIÓ CADA PRECIO — lo que se le dice al cotizador, en sus palabras.
 *
 * Los tres casos no valen lo mismo para defender un presupuesto y hasta ahora se veían
 * idénticos: el del catálogo del cliente se defiende solo, el de comercios se defiende
 * mostrando dónde se vio, y el que la inteligencia artificial estimó sin ningún comercio
 * detrás NO tiene con qué respaldarse — puede ser correcto, pero no hay nada que mostrarle a
 * quien pregunte. Ese es el que hay que poder ver de un golpe.
 *
 * `--ok` / `--accent` / `--warn` / `--bad` son variables del design system: cambian con el
 * tema claro y oscuro sin tocar nada acá.
 */
const ORIGEN_UI: Record<OrigenPrecio, { texto: string; color: string } | null> = {
  catalogo: { texto: 'De tu catálogo', color: 'var(--ok)' },
  // "Precio de mercado", no "de comercios": los comercios ya no se listan acá, y prometer
  // comercios sin mostrarlos es peor que no nombrarlos. Y tiene que decir LO MISMO que el caso
  // estimado de abajo — si uno dijera "de comercios" y el otro "de mercado", la distinción que
  // Sebastián pidió ocultar se colaría por la etiqueta.
  mercado: { texto: 'Precio de mercado', color: 'var(--accent)' },
  // DECISIÓN DE SEBASTIÁN (2026-08-14): al CLIENTE no se le dice que este precio es una
  // estimación sin comercio detrás. Se lo trata como precio de mercado, que es cierto: es una
  // referencia de mercado, solo que sin comercio citable. La distinción existe y se ve completa
  // en la auditoría de precios de staff (`/staff/precios`), que es solo para Ecosoft.
  // Si se revierte, acá vuelve 'Sin referencia de comercio' con `var(--warn)`.
  mercado_estimado: { texto: 'Precio de mercado', color: 'var(--accent)' },
  a_mano: { texto: 'Precio puesto a mano', color: 'var(--text-secondary)' },
  sin_precio: { texto: 'Sin precio', color: 'var(--bad)' },
  // Un auxiliar no tiene precio propio: se desglosa en los insumos de abajo, y cada uno
  // dice de dónde salió el suyo. Marcarlo acá sería decir algo que no significa nada.
  compuesto: null,
};

interface Origen { origen: OrigenPrecio; comercios: string[] }

/** Lee del renglón del análisis de dónde salió su precio y qué comercios lo respaldan. */
function origenDe(r: RenglonDeInsumo): Origen {
  return { origen: origenDelPrecio(r), comercios: comerciosDelRenglon(r) };
}

interface Row { desc: string; unidad: string; cantidad: string; costo: number | null; importe: number | null; nota?: string;
  /** De dónde salió el precio de este renglón (solo en los análisis armados con IA). */
  origen?: Origen;
  /** Composición de una cuadrilla: una categoría por sub-renglón (cantidad + salario). */
  children?: Array<{ desc: string; cantidad: string; costo: number | null; origen?: Origen }>; }
interface Grupo { label: string; rows: Row[]; subtotal: number | null; }

const GRUPO_ORDER = ['Materiales', 'Mano de obra', 'Equipo', 'Herramienta'] as const;
type GrupoLabel = typeof GRUPO_ORDER[number];

/** tipoCodigo OPUS → grupo (1=material,2=MO,3=herramienta,4/5=equipo,6=auxiliar,7=concepto). */
function grupoLabel(tipoCodigo?: number): GrupoLabel {
  if (tipoCodigo === 2) return 'Mano de obra';
  if (tipoCodigo === 4 || tipoCodigo === 5) return 'Equipo';
  if (tipoCodigo === 3) return 'Herramienta';
  return 'Materiales';
}

/** Matcheado: agrupa los insumos del catálogo por tipo. */
function fromInsumos(insumos: QuoteConceptInsumo[]): Grupo[] {
  const map = new Map<GrupoLabel, Row[]>();
  for (const i of insumos) {
    const g = grupoLabel(i.tipoCodigo);
    const rows = map.get(g) ?? [];
    rows.push({ desc: i.descripcion, unidad: i.unidad || '—', cantidad: num(i.cantidad, 4), costo: i.costoUnitario, importe: i.total });
    map.set(g, rows);
  }
  return GRUPO_ORDER.filter((g) => map.has(g)).map((g) => {
    const rows = map.get(g)!;
    // Redondear cada renglón ANTES de sumar (round-then-sum) para que el subtotal
    // cuadre con los importes mostrados y con m1, incluso sobre cotizaciones viejas
    // cuyos `total` guardados aún no venían redondeados.
    return { label: g, rows, subtotal: round2(rows.reduce((s, r) => s + round2(r.importe ?? 0), 0)) };
  });
}

function cuadrillaComps(
  c: unknown,
  /** El del concepto: hace falta para el operador de una máquina. Ver abajo. */
  rendimiento?: number | null,
): Array<{ cantidad: number; categoria: string; precio: number | null; origen: Origen }> {
  if (!c || typeof c !== 'object') return [];
  const obj = c as Record<string, unknown>;
  const arr = Array.isArray(obj['componentes']) ? obj['componentes'] : Array.isArray(obj['recursos']) ? obj['recursos'] : [];
  return (arr as unknown[]).map((x) => {
    const r = x as Record<string, unknown>;
    return {
      // El operador que el sistema agrega por una máquina trabaja las HORAS DE LA
      // MÁQUINA, no la jornada de la cuadrilla, y su componente lo guarda como
      // `jornadas_por_unidad`. Mostrar su `cantidad` cruda diría "1" al lado de un
      // importe que no cierra — el mismo problema que ya tuvo el equipo. La conversión
      // vive en `@supernova/shared` y la comparten esta vista y el export a m1.
      cantidad: cantidadCuadrillaPorUnidad(
        r as { cantidad?: number | null; jornadas_por_unidad?: number | null },
        rendimiento,
      ) || Number(r['jornadas'] ?? 0),
      categoria: String(r['categoria'] ?? r['descripcion'] ?? ''),
      precio: r['precio_unitario'] != null ? Number(r['precio_unitario']) : null,
      // El salario de cada categoría también sale del catálogo o del mercado, y el motor de
      // precios lo escribe en el componente igual que en un material.
      origen: origenDe(r as RenglonDeInsumo),
    };
  });
}

/** true si la MO es una cuadrilla real; false si son oficios individuales. Ausente → cuadrilla (compat). */
function esCuadrilla(c: unknown): boolean {
  return !(c && typeof c === 'object' && (c as Record<string, unknown>)['es_cuadrilla'] === false);
}

/** Composición de un AUXILIAR (recurso compuesto): sus insumos de la sub-receta
 *  (mano de obra + materiales + equipo), cada uno con su cantidad y su costo, para
 *  desglosarlo igual que una cuadrilla. */
function auxChildren(sub: ApuIaSubReceta): Array<{ desc: string; cantidad: string; costo: number | null; origen?: Origen }> {
  const out: Array<{ desc: string; cantidad: string; costo: number | null; origen?: Origen }> = [];
  // Solo lo usan los renglones de equipo HEREDADOS (base por jornada).
  const rendSub = Number(sub.rendimiento_base ?? 0);
  for (const q of cuadrillaComps(sub.cuadrilla, rendSub)) {
    out.push({ desc: q.categoria || 'Cuadrilla', cantidad: num(q.cantidad, 2), costo: q.precio, origen: q.origen });
  }
  for (const mm of sub.materiales ?? []) {
    const e = mm as typeof mm & { precio_unitario?: number | null };
    const tot = (mm.cantidad_neta ?? 0) * (1 + (mm.desperdicio_pct ?? 0) / 100);
    out.push({ desc: mm.descripcion_normalizada || '—', cantidad: num(tot, 6), costo: e.precio_unitario ?? null, origen: origenDe(mm) });
  }
  for (const eq of sub.equipo ?? []) {
    const e = eq as typeof eq & { precio_unitario?: number | null; descripcion?: string };
    out.push({
      desc: eq.descripcion_normalizada || e.descripcion || '—',
      cantidad: num(cantidadEquipoPorUnidad(e, rendSub).cantidad, 4),
      costo: e.precio_unitario ?? null,
      origen: origenDe(e),
    });
  }
  return out;
}

/** Sintético: arma los grupos desde la matriz (igual criterio que la hoja imprimible). */
function fromMatrix(c: ApuIaConcepto): Grupo[] {
  const m = c.apu_ia_matrix;
  if (!m) return [];
  const grupos: Grupo[] = [];
  const rendAdj = c.apu_ia_rendimiento_ajustado ?? m.rendimiento_base ?? 0;

  const mats: Row[] = (m.materiales ?? []).map((mm) => {
    const e = mm as typeof mm & { precio_unitario?: number | null };
    const desp = mm.desperdicio_pct ?? 0;
    const neta = mm.cantidad_neta ?? 0;
    const total = neta * (1 + desp / 100);
    const esAux = mm.tipo === 'auxiliar';
    return {
      desc: mm.descripcion_normalizada + (esAux ? ' (auxiliar)' : ''),
      unidad: mm.unidad || '—', cantidad: num(total, 6),
      // SIN precio no hay importe: va "—", no "$0.00". Un cero se lee "esto no
      // cuesta nada" y el cotizador pasa de largo; un guión se lee "falta este
      // precio" y lo hace mirar. Es la diferencia entre que se note y que no
      // (caso del mortero auxiliar sin costear, reporte de Sebastián 2026-08-10).
      costo: e.precio_unitario ?? null,
      importe: e.precio_unitario == null ? null : round2(e.precio_unitario * total),
      // La cantidad ya incluye la merma; la nota la desglosa para que sea trazable
      // (neta + % desperdicio). Se muestra SIEMPRE, incluso con 0%, para que quede
      // claro que el desperdicio se consideró (antes con 0% no salía nada y parecía
      // que no se calculaba — reporte de Sebastián en el concepto Clave2).
      nota: esAux
        ? (desp > 0 ? `neta ${num(neta, 6)} + ${num(desp, 2)}% desperdicio` : undefined)
        : `neta ${num(neta, 6)} · desperdicio ${num(desp, 2)}%`,
      origen: origenDe(mm),
      // Auxiliar = recurso compuesto → mostrar su composición (sub-receta) igual
      // que la cuadrilla, para que todo compuesto se vea desglosado.
      children: esAux && mm.sub_receta ? auxChildren(mm.sub_receta) : undefined,
    };
  });
  if (mats.length) grupos.push({ label: 'Materiales', rows: mats, subtotal: c.apu_ia_sub_materiales ?? null });

  const cuad = cuadrillaComps(m.cuadrilla, rendAdj);
  if (cuad.length || (c.apu_ia_sub_mo ?? 0) > 0) {
    const cantMo = rendAdj > 0 ? 1 / rendAdj : 0;
    const tipoCuad = m.cuadrilla?.tipo_cuadrilla;
    let moRows: Row[];
    if (esCuadrilla(m.cuadrilla) || cuad.length === 0) {
      // Cuadrilla real → un renglón "Cuadrilla" con su COMPOSICIÓN desglosada
      // debajo (una categoría por sub-renglón, con su cantidad en la cuadrilla y
      // su salario), para que se vea como cuadrilla y se pueda revisar de qué se
      // compone (antes salía todo concatenado en una sola celda).
      moRows = [{
        desc: 'Cuadrilla' + (tipoCuad ? ` · ${tipoCuad}` : ''),
        unidad: 'jornada', cantidad: num(cantMo, 6), costo: c.apu_ia_sr_cuadrilla ?? null, importe: c.apu_ia_sub_mo ?? null,
        children: cuad.map((q) => ({
          desc: q.categoria || '—',
          cantidad: num(q.cantidad, 2),
          costo: q.precio,
          origen: q.origen,
        })),
      }];
    } else {
      // Oficios individuales → un renglón por oficio (jornadas del oficio = nº × cantMo).
      moRows = cuad.map((q) => {
        const cant = q.cantidad * cantMo;
        return {
          desc: q.categoria || '—', unidad: 'jornada', cantidad: num(cant, 6),
          // Sin salario resuelto, importe en "—" (ver materiales, misma razón).
          costo: q.precio, importe: q.precio == null ? null : round2(q.precio * cant),
          origen: q.origen,
        };
      });
    }
    grupos.push({ label: 'Mano de obra', rows: moRows, subtotal: c.apu_ia_sub_mo ?? null });
  }

  const eqs: Row[] = (m.equipo ?? []).map((eq) => {
    const e = eq as typeof eq & { precio_unitario?: number | null; descripcion?: string };
    // La cantidad que se MUESTRA es la misma que se COSTEA: horas por unidad del
    // concepto (`cantidadEquipoPorUnidad`, la regla compartida con el worker).
    // Antes acá se mostraban las horas por JORNADA al lado de un importe ya
    // prorrateado por el rendimiento, y el renglón no cerraba en pantalla:
    // 6 × 124 daba 744 y mostraba 93.
    const { cantidad: qty } = cantidadEquipoPorUnidad(e, rendAdj);
    // Igual que en materiales: sin precio, importe en "—" y no en cero.
    const imp = e.precio_unitario == null ? null : round2(e.precio_unitario * qty);
    return {
      desc: eq.descripcion_normalizada ?? e.descripcion ?? '—',
      unidad: 'hora', cantidad: num(qty, 4), costo: e.precio_unitario ?? null, importe: imp,
      origen: origenDe(e),
    };
  });
  if (eqs.length) grupos.push({ label: 'Equipo', rows: eqs, subtotal: c.apu_ia_sub_equipo ?? null });

  if ((c.apu_ia_sub_herramienta ?? 0) > 0) {
    // Herramienta menor = porcentaje SOBRE la mano de obra. El % se refleja en la
    // UNIDAD ("%") y la CANTIDAD (el porcentaje), con el subtotal de MO como base
    // en COSTO para que el cálculo quede trazable. Antes salían todos en "—" y
    // solo el importe reflejaba el 3% (reporte del tester).
    grupos.push({
      label: 'Herramienta',
      rows: [{
        desc: 'Herramienta menor',
        // Convención OPUS: unidad "%MO" = porcentaje SOBRE mano de obra (así como
        // habría %MAT sobre materiales, %EQ sobre equipo). La cantidad es el
        // porcentaje y el costo es la base (subtotal MO) para que sea trazable.
        unidad: '%MO',
        cantidad: num(m.herramienta_pct ?? 0, 2),
        costo: c.apu_ia_sub_mo ?? null,
        importe: c.apu_ia_sub_herramienta ?? null,
      }],
      subtotal: c.apu_ia_sub_herramienta ?? null,
    });
  }
  return grupos;
}

/**
 * DE DÓNDE SALIÓ EL PRECIO DE ESTE RENGLÓN, en una línea chica debajo del recurso.
 * Cuando hay comercios se nombran: son justamente lo que se le puede mostrar a un cliente
 * que pregunta. Se escriben TODOS y la línea envuelve — nada de cortar el texto ni esconderlo
 * detrás de un hover, que en tablet no existe.
 */
function OrigenLinea({ origen, tamano = 10 }: { origen: Origen | undefined; tamano?: number }) {
  if (!origen) return null;
  const ui = ORIGEN_UI[origen.origen];
  if (!ui) return null;
  // Los comercios NO se listan acá (Sebastián, 2026-08-14). Para la mano de obra son URLs de
  // portales de empleo —indeed, occ, facebook— ilegibles y sin valor para quien cotiza. El
  // detalle completo, con su país y su fecha, vive en la auditoría de precios de staff.
  const conComercios = false;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 4, fontSize: tamano, lineHeight: 1.35, color: ui.color, fontWeight: 600, whiteSpace: 'normal' }}>
      {/* El punto se alinea con la PRIMERA línea: cuando la lista de comercios envuelve en
          varias, centrarlo lo dejaba flotando a la mitad del bloque. */}
      <span aria-hidden style={{ flexShrink: 0, marginTop: tamano * 0.675 - 2.5, width: 5, height: 5, borderRadius: '50%', background: ui.color }} />
      <span>
        {ui.texto}
        {conComercios && (
          <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>: {origen.comercios.join(', ')}</span>
        )}
      </span>
    </span>
  );
}

/**
 * EL RESUMEN DE ARRIBA: cuántos insumos de este análisis salieron por cada camino. Es un
 * recuento de lo que se ve abajo, nada más — el veredicto de si el análisis está bien
 * respaldado no se decide acá, lo dice el aviso del panel con los umbrales de Ecosoft.
 */
function ResumenOrigenes({ grupos }: { grupos: Grupo[] }) {
  // Se cuentan las HOJAS: los renglones con precio propio, incluidos los insumos que
  // cuelgan de una cuadrilla o de un auxiliar. El compuesto que se desglosa no cuenta
  // (no tiene precio propio) y el recuento lo deja fuera solo.
  const hojas: OrigenPrecio[] = [];
  for (const g of grupos) {
    for (const r of g.rows) {
      if (r.children?.length) { for (const ch of r.children) { if (ch.origen) hojas.push(ch.origen.origen); } }
      else if (r.origen) hojas.push(r.origen.origen);
    }
  }
  const c = contarOrigenesClasificados(hojas);
  if (c.total === 0) return null;
  const pills: Array<{ n: number; texto: string; color: string }> = [
    { n: c.catalogo, texto: 'de tu catálogo', color: 'var(--ok)' },
    // Los de mercado con comercio y los estimados van JUNTOS en el recuento del cliente
    // (decisión de Sebastián 2026-08-14, ver ORIGEN_UI): para él son los dos "de mercado".
    { n: c.mercado + c.mercadoEstimado, texto: 'con precio de mercado', color: 'var(--accent)' },
    { n: c.aMano, texto: c.aMano === 1 ? 'puesto a mano' : 'puestos a mano', color: 'var(--text-secondary)' },
    { n: c.sinPrecio, texto: 'sin precio', color: 'var(--bad)' },
  ].filter((p) => p.n > 0);
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)', fontWeight: 700, marginBottom: 5 }}>
        De dónde sale el precio de cada insumo
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {pills.map((p) => (
          <span
            key={p.texto}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, lineHeight: 1.3,
              color: 'var(--text-secondary)', background: 'var(--bg-canvas)',
              border: '1px solid var(--border-subtle)', borderRadius: 999, padding: '3px 10px',
            }}
          >
            <span aria-hidden style={{ flexShrink: 0, width: 6, height: 6, borderRadius: '50%', background: p.color }} />
            <b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{p.n}</b> {p.texto}
          </span>
        ))}
      </div>
      {/* El aviso de "precio estimado sin comercio que mostrar" NO va acá: es información para
          Ecosoft, no para el cliente (decisión de Sebastián 2026-08-14). Vive en la auditoría de
          precios de staff, donde se ve el detalle completo con sus comercios y su país. */}
    </div>
  );
}

function Tablas({ grupos, precioUnitario, money, resumenOrigenes = false }: {
  grupos: Grupo[];
  precioUnitario: number | null;
  /** Formateador de importes en la moneda de la obra (ver lib/money.ts). */
  money: (n: number | null | undefined) => string;
  resumenOrigenes?: boolean;
}) {
  if (grupos.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>No hay desglose de precio unitario para este concepto.</div>;
  }
  const th: React.CSSProperties = { textAlign: 'right', padding: '3px 8px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--text-tertiary)', fontWeight: 600 };
  const td: React.CSSProperties = { textAlign: 'right', padding: '4px 8px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* El recuento "De dónde sale el precio de cada insumo" con sus pastillas se QUITÓ del
          panel del cliente (Sebastián, 2026-08-14). El dato se sigue calculando y se ve en la
          auditoría de precios de staff. Para reponerlo: <ResumenOrigenes grupos={grupos} />. */}
      {grupos.map((g) => (
        <div key={g.label}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)', marginBottom: 3, borderTop: '2px solid var(--border-subtle)', paddingTop: 6 }}>{g.label}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 0 }}>Recurso</th>
              <th style={{ ...th, textAlign: 'left' }}>Unidad</th>
              <th style={th}>Cantidad</th>
              <th style={th}>Costo</th>
              <th style={{ ...th, paddingRight: 0 }}>Importe</th>
            </tr></thead>
            <tbody>
              {g.rows.map((r, i) => (
                <Fragment key={i}>
                  <tr style={{ borderTop: i > 0 ? '1px dashed var(--border-subtle)' : 'none' }}>
                    <td style={{ padding: '4px 8px 4px 0', color: 'var(--text-primary)', verticalAlign: 'top' }}>
                      {r.desc}
                      {/* De dónde salió su precio, y —cuando el desperdicio aplica— la nota
                          de siempre, en la MISMA línea chica para no estirar la tabla. */}
                      {(r.origen || r.nota) && (
                        <div style={{ marginTop: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 6, rowGap: 1 }}>
                          {/* La línea de origen del precio ("De tu catálogo" / "Precio de mercado")
                              se QUITÓ del panel del cliente (Sebastián, 2026-08-14). Se ve completa,
                              con comercios, país y fecha, en /staff/precios. Reponer: <OrigenLinea
                              origen={r.origen} />. */}
                          {r.nota && <span style={{ fontSize: 10, lineHeight: 1.35, color: 'var(--text-tertiary)' }}>{r.nota}</span>}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'left', color: 'var(--text-tertiary)', verticalAlign: 'top' }}>{r.unidad}</td>
                    <td style={{ ...td, color: 'var(--text-secondary)', verticalAlign: 'top' }}>{r.cantidad || '—'}</td>
                    <td style={{ ...td, color: 'var(--text-secondary)', verticalAlign: 'top' }}>{r.costo != null ? money(r.costo) : '—'}</td>
                    <td style={{ ...td, paddingRight: 0, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', verticalAlign: 'top' }}>{r.importe != null ? money(r.importe) : '—'}</td>
                  </tr>
                  {/* Composición de la cuadrilla: una categoría por sub-renglón. */}
                  {r.children?.map((ch, j) => (
                    <tr key={`c${j}`}>
                      <td style={{ padding: '2px 8px 2px 16px', color: 'var(--text-tertiary)', fontSize: 11.5, verticalAlign: 'top' }}>
                        ↳ {ch.desc}
                        {/* ídem para los sub-renglones: reponer con OrigenLinea y ch.origen. */}
                      </td>
                      <td />
                      <td style={{ ...td, color: 'var(--text-tertiary)', fontSize: 11.5, verticalAlign: 'top' }}>{ch.cantidad}</td>
                      <td style={{ ...td, color: 'var(--text-tertiary)', fontSize: 11.5, verticalAlign: 'top' }}>{ch.costo != null ? money(ch.costo) : '—'}</td>
                      <td />
                    </tr>
                  ))}
                </Fragment>
              ))}
              {g.subtotal != null && (
                <tr style={{ borderTop: '1px solid var(--border-strong)' }}>
                  <td colSpan={4} style={{ padding: '5px 8px 5px 0', textAlign: 'right', fontWeight: 700, color: 'var(--text-secondary)', fontSize: 11 }}>Subtotal {g.label.toLowerCase()}</td>
                  <td style={{ ...td, paddingRight: 0, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{money(g.subtotal)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ))}
      {precioUnitario != null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderTop: '2px solid var(--border-strong)', paddingTop: 10, marginTop: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Precio unitario · costo directo</span>
          <span style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{money(precioUnitario)}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Desglose read-only de un concepto de CATÁLOGO (matcheado) a partir de sus insumos ya cargados
 * — misma tabla que la cotización (fromInsumos + Tablas). Lo usa /planos para mostrar la receta
 * del recurso del catálogo. Si el concepto no trae receta (insumos vacíos), Tablas muestra el
 * aviso "No hay desglose…".
 */
export function InsumosTables({
  insumos,
  precioUnitario,
  moneda,
}: {
  insumos: QuoteConceptInsumo[];
  precioUnitario?: number | null;
  moneda?: string | null;
}) {
  const money = useMemo(() => formatoDeMoneda(moneda).money, [moneda]);
  return <Tablas grupos={fromInsumos(insumos)} precioUnitario={precioUnitario ?? null} money={money} />;
}

/**
 * Desglose read-only de un APU sintético (materiales / MO / equipo / herramienta) a partir de un
 * `ApuIaConcepto` YA cargado — sin fetch ni acoplarse a la cotización. Lo usa /planos para mostrar
 * el análisis del sintético de un renglón. Misma tabla que la cotización (fromMatrix + Tablas).
 */
export function ApuMatrixTables({
  concepto,
  moneda,
}: {
  concepto: ApuIaConcepto;
  moneda?: string | null;
}) {
  const money = useMemo(() => formatoDeMoneda(moneda).money, [moneda]);
  return (
    <Tablas
      grupos={fromMatrix(concepto)}
      precioUnitario={concepto.apu_ia_costo_directo ?? null}
      money={money}
    />
  );
}

export function ConceptAnalysisView({
  quoteId,
  concepto,
  preloadedApu,
  moneda,
}: {
  quoteId: string;
  concepto: QuoteConcept;
  /** Matriz ya cargada por el contenedor (ej. el panel lateral) para evitar
   *  un segundo fetch del mismo endpoint. Si se pasa, no se vuelve a pedir. */
  preloadedApu?: ApuIaConcepto | null;
  /** Moneda de la obra (ISO 4217). `null`/ausente = no se sabe → sin símbolo. */
  moneda?: string | null;
}) {
  const money = useMemo(() => formatoDeMoneda(moneda).money, [moneda]);

  // El desglose que se muestra es el del precio que HOY cuenta en el total. Un concepto
  // puede tener guardados los dos —el de su catálogo y el armado con inteligencia
  // artificial— y `apuIaJobRunId` sigue puesto aunque se haya vuelto al del catálogo: con
  // esa condición vieja, el panel mostraba la composición de un precio que ya no se usa.
  const isSynthetic = fuenteActivaDelPrecio(concepto) === 'ia';
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [apu, setApu] = useState<ApuIaConcepto | null>(preloadedApu ?? null);

  useEffect(() => {
    if (!isSynthetic) return;
    if (preloadedApu) { setApu(preloadedApu); setLoading(false); setErr(null); return; }
    let alive = true;
    setLoading(true);
    setErr(null);
    getApuIaConceptMatrix(quoteId, concepto.id)
      .then((r) => { if (alive) setApu(r.concepto); })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : 'No se pudo cargar el análisis'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isSynthetic, quoteId, concepto.id, preloadedApu]);

  if (isSynthetic) {
    if (loading) return <div style={{ padding: '6px 0', fontSize: 12, color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 8 }}><Spinner size="extra-tiny" /> Cargando análisis…</div>;
    if (err) return <div style={{ padding: '6px 0', fontSize: 12, color: 'var(--bad)' }}>{err}</div>;
    if (!apu) return <div style={{ padding: '6px 0', fontSize: 12, color: 'var(--text-tertiary)' }}>Análisis no disponible.</div>;
    return <Tablas grupos={fromMatrix(apu)} precioUnitario={apu.apu_ia_costo_directo ?? concepto.precioUnitario} money={money} />;
  }
  // MATCHEADO: la composición es la del concepto de su catálogo, así que todos sus insumos
  // y todos sus precios salen de ahí. No hace falta marcar renglón por renglón —basta
  // decirlo una vez— pero sí hace falta DECIRLO: es la diferencia con un análisis armado
  // con inteligencia artificial, y es lo que hace que este precio se defienda solo.
  const grupos = fromInsumos(concepto.insumos ?? []);
  return (
    <>
      {grupos.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 11.5, lineHeight: 1.4,
          color: 'var(--text-secondary)', marginBottom: 8,
        }}>
          <span aria-hidden style={{ flexShrink: 0, alignSelf: 'center', width: 6, height: 6, borderRadius: '50%', background: 'var(--ok)' }} />
          <span>Todos estos insumos, y su precio, salen de tu catálogo.</span>
        </div>
      )}
      <Tablas grupos={grupos} precioUnitario={concepto.precioUnitario} money={money} />
    </>
  );
}
